/**
 * TAXONOMY CONTRACT — fixed ids shared by the data pipeline, icons and UI.
 * Owned by the main loop. Erasable TS only (imported by Node scripts).
 */
import type { Category, Group } from "./data.ts";

export const GROUPS: Group[] = [
  { id: "A", label: "Physical infrastructure", short: "Infrastructure", icon: "road", order: 0 },
  { id: "B", label: "Social infrastructure", short: "Social", icon: "school", order: 1 },
  { id: "C", label: "Employment", short: "Employment", icon: "briefcase", order: 2 },
  { id: "D", label: "Residential benchmarks", short: "Benchmarks", icon: "township", order: 3 },
];

/** The 14 sub-categories, in Excel order. `sourceLabel` is the exact Excel text. */
export const CATEGORIES: Category[] = [
  { id: "arterial-roads", group: "A", label: "Arterial roads", sourceLabel: "Arterial Road Network", order: 0, icon: "road", kind: "road" },
  { id: "future-infrastructure", group: "A", label: "Future infrastructure", sourceLabel: "Future infrastructure", order: 1, icon: "ringRoad", kind: "future" },
  { id: "logistic-hubs", group: "A", label: "Airports and rail", sourceLabel: "Logistic Hubs", order: 2, icon: "plane", kind: "hub" },
  { id: "education", group: "B", label: "Education", sourceLabel: "Education", order: 0, icon: "school", kind: "poi" },
  { id: "healthcare", group: "B", label: "Healthcare", sourceLabel: "Healthcare", order: 1, icon: "hospital", kind: "poi" },
  { id: "lifestyle-retail", group: "B", label: "Lifestyle and retail", sourceLabel: "Lifestyle / retail", order: 2, icon: "bag", kind: "poi" },
  { id: "urban-centres", group: "B", label: "Urban centres", sourceLabel: "Urban centres", order: 3, icon: "city", kind: "area" },
  { id: "hospitality", group: "B", label: "Hospitality", sourceLabel: "Hospitality", order: 4, icon: "hotel", kind: "poi" },
  { id: "business-parks", group: "C", label: "Business parks", sourceLabel: "Employment", order: 0, icon: "briefcase", kind: "poi" },
  { id: "industrial", group: "C", label: "Industrial belts", sourceLabel: "Industrial / economic", order: 1, icon: "factory", kind: "poi" },
  { id: "premium-benchmarks", group: "D", label: "Premium benchmarks", sourceLabel: "Premium benchmarks", order: 0, icon: "tower", kind: "poi" },
  { id: "township-benchmarks", group: "D", label: "Township benchmarks", sourceLabel: "Township benchmark", order: 1, icon: "township", kind: "poi" },
  { id: "neighbouring-benchmarks", group: "D", label: "Neighbouring benchmarks", sourceLabel: "Neighbouring benchmarks", order: 2, icon: "homes", kind: "poi" },
  { id: "upcoming-townships", group: "D", label: "Upcoming townships", sourceLabel: "Upcoming Townships", order: 3, icon: "sprout", kind: "poi" },
];

/** Feature ids in roads.geojson (existing) and future.geojson (proposed). Places of
 *  kind road/future reference these via Place.geometryRef. */
export const CORRIDOR_IDS = {
  // roads.geojson
  dwpRoad: "dwp-road", //                 Diamond Water Park Road
  lohegaonWagholi: "lohegaon-wagholi-road",
  nagar: "nagar-road", //                 Pune–Nagar Road, NH753F
  solapur: "solapur-road", //             Pune–Solapur Road, NH65
  alandi: "alandi-road", //               Pune–Alandi Road
  nashik: "nashik-road", //               Pune–Nashik Road, NH60
  mumbai: "mumbai-road", //               Pune–Mumbai (NH48 / Mumbai–Pune Expressway)
  kolhapur: "kolhapur-road", //           Pune–Kolhapur–Goa Road, NH48 south
  saswad: "saswad-road", //               Pune–Saswad Road, NH965
  // future.geojson
  innerRing: "inner-ring-road", //        PMRDA Inner Ring Road — schematic through Excel connection pins
  lohegaonCorridors: "lohegaon-corridors", // Urban Lohegaon connectivity corridors (DP) — indicative
  irrOrrConnection: "irr-orr-connection", //  S.No. 41 foothill road → Outer Ring Road — indicative
  outerRing: "outer-ring-road", //         MSRDC Pune Ring Road (OSM, under construction / proposed)
} as const;
export type CorridorId = (typeof CORRIDOR_IDS)[keyof typeof CORRIDOR_IDS];

/** Icons the UI and data rely on. src/design/icons.tsx MUST export all of these
 *  (in addition to the 60 ported from the aesthetic file). New icons follow the
 *  aesthetic's rules: 24 px grid, 1.5 stroke, round caps/joins, drawn loosely. */
export const REQUIRED_ICONS = [
  // new, category-level
  "road", "ringRoad", "plane", "train", "school", "hospital", "bag", "city", "hotel",
  "briefcase", "factory", "tower", "township", "sprout",
  // new, UI-level
  "car", "route", "compass", "target", "university", "mapPin", "external",
  // ported from the aesthetic (subset actually used here)
  "masterplan", "landscape", "homes", "list", "walk", "globe", "close", "chevL", "chevR", "chevD", "chevU",
  "plus", "minus", "recenter", "expand", "share", "search", "sun", "moon", "sunrise", "sunset", "layers",
  "elevation", "tree", "waves", "path", "info", "check", "sort", "play", "pause", "pin", "clock", "arrowUpRight", "filter",
] as const;
export type RequiredIcon = (typeof REQUIRED_ICONS)[number];

/** Distance rings drawn around the site, km. Echoes master plan p6. */
export const RING_KM = [5, 10, 15, 25] as const;

/** Map-wide constants. */
export const SITE_PIN: [number, number] = [73.9574802, 18.6065759]; // Diamond Water Parks = SW corner of Ládera City
/** The region packs (basemap z8–11, DEM z0–9, region contours) cover exactly this box, and it is the map's maxBounds.
 *  East edge 74.25 → 74.55 (2026-09-25, QA L1-01b): Ranjangaon MIDC (74.289E) and Purandar (74.134E) now frame
 *  beside the open card at 1440×900 and 1920×1080. Rebuild with scripts/map/build-map-packs.ps1 and build_contours.py. */
export const REGION_BBOX: [number, number, number, number] = [72.75, 18.2, 74.55, 19.3];
export const METRO_BBOX: [number, number, number, number] = [73.75, 18.4, 74.15, 18.72];
export const CORE_BBOX: [number, number, number, number] = [73.88, 18.55, 74.02, 18.65];
